import { useState, useEffect } from 'react';
import { adminApi } from '../services/AdminApiService';
import { AdminPanelState, PromptFormData } from '../types/admin';

export const useAdminPanel = () => {
  const [state, setState] = useState<AdminPanelState>({
    activeTab: 'list',
    selectedPrompt: null,
    prompts: [],
    loading: false,
    error: null
  });

  const loadPrompts = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const prompts = await adminApi.getPrompts();
      setState(prev => ({ ...prev, prompts, loading: false }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Error loading prompts',
        loading: false 
      }));
    }
  };

  const selectPrompt = async (id: number) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const prompt = await adminApi.getPrompt(id);
      setState(prev => ({ 
        ...prev, 
        selectedPrompt: prompt, 
        activeTab: 'edit',
        loading: false 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Error loading prompt',
        loading: false 
      }));
    }
  };

  const createPrompt = async (data: PromptFormData) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await adminApi.createPrompt(data);
      await loadPrompts();
      setState(prev => ({ ...prev, activeTab: 'list', loading: false }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Error creating prompt',
        loading: false 
      }));
    }
  };

  const updatePrompt = async (id: number, data: Partial<PromptFormData>) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await adminApi.updatePrompt(id, data);
      await loadPrompts();
      setState(prev => ({ 
        ...prev, 
        selectedPrompt: null,
        activeTab: 'list',
        loading: false 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Error updating prompt',
        loading: false 
      }));
    }
  };

  const deletePrompt = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este prompt?')) return;
    
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await adminApi.deletePrompt(id);
      await loadPrompts();
      setState(prev => ({ 
        ...prev, 
        selectedPrompt: null,
        activeTab: 'list',
        loading: false 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Error deleting prompt',
        loading: false 
      }));
    }
  };

  const duplicatePrompt = async (id: number, newName?: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await adminApi.duplicatePrompt(id, newName);
      await loadPrompts();
      setState(prev => ({ ...prev, loading: false }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Error duplicating prompt',
        loading: false 
      }));
    }
  };

  const setActiveTab = (tab: AdminPanelState['activeTab']) => {
    setState(prev => ({ ...prev, activeTab: tab }));
  };

  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  useEffect(() => {
    loadPrompts();
  }, []);

  return {
    activeTab: state.activeTab,
    selectedPrompt: state.selectedPrompt,
    prompts: state.prompts,
    loading: state.loading,
    error: state.error,
    actions: {
      loadPrompts,
      selectPrompt,
      createPrompt,
      updatePrompt,
      deletePrompt,
      duplicatePrompt,
      setActiveTab,
      clearError
    }
  };
};
